import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { SECTORS, STAGES, IMAGES } from "../../data";
import { Reveal, Kicker } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { founder_name: "", startup_name: "", email: "", sector: SECTORS[0], stage: STAGES[0], pitch: "", deck_url: "" };

const field = "w-full rounded-xl border border-borderln bg-sand px-4 py-3 text-ink placeholder:text-stoneink/50 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition";

export default function ContactForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.founder_name || !form.startup_name || !form.email || !form.pitch) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      toast.success("Application received", { description: "Thank you — our partners will be in touch soon.", duration: 5000 });
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form-section" data-testid="contact-form-section" className="py-24 lg:py-36 bg-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <Reveal><Kicker>Apply</Kicker></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-[1.08]">Begin the conversation</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-stoneink leading-relaxed">Share your venture and the values that move it. We read every submission with care and respond within two weeks.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 relative aspect-[16/10] rounded-3xl overflow-hidden border border-brass/40">
              <img src={IMAGES.lotusWater} alt="Lotus" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="rounded-3xl border border-borderln bg-cardw p-7 sm:p-10 space-y-5 shadow-[0_30px_80px_-40px_rgba(140,98,33,0.4)]">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Founder Name *</label>
                <input data-testid="apply-form-name-input" className={field} value={form.founder_name} onChange={set("founder_name")} placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Startup Name *</label>
                <input data-testid="apply-form-startup-input" className={field} value={form.startup_name} onChange={set("startup_name")} placeholder="Your venture" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Email *</label>
              <input data-testid="apply-form-email-input" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@venture.com" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Sector Focus</label>
                <select data-testid="apply-form-sector-select" className={field} value={form.sector} onChange={set("sector")}>
                  {SECTORS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-2">Stage</label>
                <select data-testid="apply-form-stage-select" className={field} value={form.stage} onChange={set("stage")}>
                  {STAGES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Value Alignment Pitch *</label>
              <textarea data-testid="apply-form-pitch-textarea" rows={4} className={field} value={form.pitch} onChange={set("pitch")} placeholder="How does your venture embody integral values?" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Deck URL</label>
              <input data-testid="apply-form-deck-input" className={field} value={form.deck_url} onChange={set("deck_url")} placeholder="https://..." />
            </div>
            <motion.button data-testid="apply-form-submit-btn" whileTap={{ scale: 0.98 }} disabled={loading} type="submit"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-sand px-7 py-4 font-medium hover:bg-saffron transition-colors duration-300 disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Application <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
